import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';

interface Notification {
  id: number;
  title: string;
  message?: string;
  link?: string;
  is_read: boolean;
  created_at: string;
}

const POLL_INTERVAL = 30000; // 30 soniya

const timeAgo = (dateStr: string): string => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'hozir';
  if (min < 60) return `${min} daqiqa oldin`;
  const hours = Math.floor(min / 60);
  if (hours < 24) return `${hours} soat oldin`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} kun oldin`;
  return new Date(dateStr).toLocaleDateString('ru-RU');
};

const NotificationBell: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await api.get('/notifications/unread-count');
      setUnreadCount(res.data.count || 0);
    } catch (e) { /* sukutda */ }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get('/notifications', { params: { limit: 20 } });
      setNotifications(res.data);
    } catch (e) { setNotifications([]); }
  }, []);

  // Polling — o'qilmaganlar sonini muntazam yangilab turish
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Tashqariga bosilganda yopish
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggleOpen = async () => {
    const next = !open;
    setOpen(next);
    if (next) await fetchNotifications();
  };

  const handleClick = async (n: Notification) => {
    if (!n.is_read) {
      try {
        await api.put(`/notifications/${n.id}/read`);
        setUnreadCount(c => Math.max(0, c - 1));
        setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, is_read: true } : x));
      } catch (e) { /* sukutda */ }
    }
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  const markAllRead = async () => {
    try {
      await api.put('/notifications/read-all');
      setUnreadCount(0);
      setNotifications(prev => prev.map(x => ({ ...x, is_read: true })));
    } catch (e) { /* sukutda */ }
  };

  const handleDelete = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    try {
      await api.delete(`/notifications/${id}`);
      setNotifications(prev => prev.filter(x => x.id !== id));
      fetchUnreadCount();
    } catch (e) { /* sukutda */ }
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={toggleOpen}
        className="relative p-2 rounded-lg hover:bg-gray-100 transition"
        title="Bildirishnomalar">
        <span className="text-xl">🔔</span>
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed inset-x-3 top-16 sm:absolute sm:inset-x-auto sm:top-auto sm:right-0 sm:mt-2 w-auto sm:w-80 bg-white rounded-xl shadow-lg border border-gray-100 z-50 max-h-[28rem] overflow-hidden flex flex-col">
          <div className="flex justify-between items-center px-4 py-3 border-b">
            <h4 className="font-semibold text-gray-700 text-sm">Bildirishnomalar</h4>
            {notifications.some(n => !n.is_read) && (
              <button onClick={markAllRead}
                className="text-xs text-blue-500 hover:underline">
                Hammasini o'qilgan deb belgilash
              </button>
            )}
          </div>

          <div className="overflow-y-auto flex-1">
            {notifications.length === 0 ? (
              <p className="text-center text-gray-400 text-sm py-8">Bildirishnomalar yo'q</p>
            ) : (
              notifications.map(n => (
                <div key={n.id}
                  onClick={() => handleClick(n)}
                  className={`px-4 py-3 border-b last:border-b-0 cursor-pointer transition hover:bg-gray-50 group ${
                    n.is_read ? '' : 'bg-blue-50/50'
                  }`}>
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {!n.is_read && <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />}
                        <p className={`text-sm truncate ${n.is_read ? 'text-gray-600' : 'font-semibold text-gray-800'}`}>
                          {n.title}
                        </p>
                      </div>
                      {n.message && (
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.message}</p>
                      )}
                      <p className="text-[11px] text-gray-400 mt-1">{timeAgo(n.created_at)}</p>
                    </div>
                    <button onClick={(e) => handleDelete(e, n.id)}
                      className="text-gray-300 hover:text-red-500 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition text-base sm:text-xs p-1 -m-1">
                      ✕
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
